// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import mainLogo from '../assets/images/new/logoCC.png';

const AnimatedHamburgerIcon = ({ isOpen, toggle }) => {
    return (
        <button onClick={toggle} className="fixed top-4 left-4 z-50 md:hidden">
            <svg width="24" height="24" viewBox="0 0 24 24">
                <motion.path
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    stroke="black"
                    fill="transparent"
                    d="M 3 6 L 21 6"
                    animate={isOpen ? { d: "M 4 4 L 20 20" } : { d: "M 3 6 L 21 6" }}
                />
                <motion.path
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    stroke="black"
                    fill="transparent"
                    d="M 3 12 L 21 12"
                    animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                />
                <motion.path
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    stroke="black"
                    fill="transparent"
                    d="M 3 18 L 21 18"
                    animate={isOpen ? { d: "M 4 20 L 20 4" } : { d: "M 3 18 L 21 18" }}
                />
            </svg>
        </button>
    );
};

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const { language, toggleLanguage } = useLanguage();
    const [isScrolled, setIsScrolled] = useState(false);
    const activeSectionRef = useRef(activeSection);

    useEffect(() => {
        activeSectionRef.current = activeSection;
    }, [activeSection]);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
            const sections = ['home',
                'about',
                'services',
                'projects',
                // 'contact'
            ];
            let current = null;

            for (const id of sections) {
                const el = document.getElementById(id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top <= window.innerHeight * 0.4 && rect.bottom >= 150) {
                        current = id;
                        break;
                    }
                }
            }

            if (current && current !== activeSectionRef.current) {
                setActiveSection(current);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const offset = -60;
            const y = el.getBoundingClientRect().top + window.pageYOffset + offset;
            window.scrollTo({ top: y, behavior: 'smooth' });
            setActiveSection(id);
            setIsOpen(false);
        }
    };

    const navItems = {
        en: ['Home',
            'About',
            'Services',
            'Projects',
            // 'Contact'
        ],
        ar: ['الرئيسية',
            'خدماتنا',
            'من نحن',
            'مشاريعنا',
            // 'اتصل بنا'
        ],
    };

    const sectionIds = ['home', 'about', 'services', 'projects', 'contact'];

    return (
        <>
            {/* Hamburger */}
            <AnimatedHamburgerIcon isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} />

            {/* Sidebar */}
            <motion.nav
                initial={false}
                animate={isOpen || window.innerWidth >= 768 ? 'open' : 'closed'}
                variants={{
                    open: { x: 0 },
                    closed: { x: '-100%' },
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                // MODIFIED: Removed `justify-center` to allow items to stack from the top.
                className={`fixed left-0 top-0 z-40 h-full w-64 bg-white/30 backdrop-blur-lg border-r border-white/20 
                    md:translate-x-0 px-6 py-10 flex flex-col`}
            >
                {/* Logo - This will now stay at the top */}
                <div
                    className="text-center text-xl font-greycliff font-bold mb-10 cursor-pointer"
                    onClick={() => scrollTo('home')}
                >
                    <img src={mainLogo} className="mx-auto h-auto w-20" alt="logo" />
                </div>

                {/* MODIFIED: `flex-grow` and `justify-center` added here.
                    The list will grow to fill available space and center its children vertically. */}
                <ul className="flex-grow flex flex-col justify-center text-center space-y-4">
                    {navItems[language].map((label, idx) => {
                        const id = sectionIds[idx];
                        const isActive = activeSection === id;

                        return (
                            <motion.li
                                key={id}
                                whileHover={{ x: 6 }}
                                className={`relative cursor-pointer px-3 py-2 rounded-md text-lg font-greycliff font-bold ${
                                    isActive
                                        ? 'text-[#28826F] bg-white/40 shadow-inner'
                                        : 'text-gray-700 hover:text-black'
                                }`}
                                onClick={() => scrollTo(id)}
                            >
                                {label}
                            </motion.li>
                        );
                    })}
                </ul>

                {/* Language Switcher remains commented out at the bottom */}
                {/*
                <motion.button
                    onClick={toggleLanguage}
                    className="mt-10 py-3 px-4 rounded-lg bg-white/40 hover:bg-white/60
                     border border-white/30 text-gray-800 font-semibold text-sm transition"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                >
                    🌐
                </motion.button>
                */}
            </motion.nav>

            {/* Dark overlay (mobile only) */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 bg-black/30 z-30 md:hidden"
                        onClick={() => setIsOpen(false)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;